import dayjs from 'dayjs';

import { IAuthUser } from 'auth/types';
import { DB_DATE_FORMAT } from 'core/constants';
import { AccessPermissions, AgGridFilterOption } from 'core/enums';
import { QuoteEntityType, QuoteState, QuoteStatus } from 'quotes/enums';

export const getInitialState = (user: IAuthUser, entityType = QuoteEntityType.QUOTE) => {
  switch (entityType) {
    case QuoteEntityType.ORDER:
      return {
        filter: {
          filterModel: {
            customerCorporateUserName: {
              filterType: 'text',
              type: AgGridFilterOption.CONTAINS,
              filter: `${user?.firstName} ${user?.lastName}`,
            },
            archived: { filterType: 'set', values: [QuoteState.ACTIVE] },
            createdAt: {
              filterType: 'date',
              type: AgGridFilterOption.RELATIVE_LAST_30_DAYS,
            },
          },
        },
      };
    case QuoteEntityType.QUOTE:
    default:
      return {
        filter: {
          filterModel: {
            customerCorporateUserName: {
              filterType: 'text',
              type: AgGridFilterOption.CONTAINS,
              filter: `${user?.firstName} ${user?.lastName}`,
            },
            validUntil: {
              filterType: 'date',
              type: AgGridFilterOption.GREATER_THAN,
              dateFrom: dayjs().format(DB_DATE_FORMAT),
            },
            archived: { filterType: 'set', values: [QuoteState.ACTIVE] },
          },
        },
      };
  }
};

interface IQuoteStatusOptions {
  // Imported VQs are not valid add-to-quote destinations, so the copy-parts picker must not offer
  // "Imported" in its status filter. No-op for the ORDER entity, which has no imported status.
  excludeImported?: boolean;
}

export const getQuoteStatuses = (
  entityType: QuoteEntityType,
  { excludeImported = false }: IQuoteStatusOptions = {}
): QuoteStatus[] => {
  switch (entityType) {
    case QuoteEntityType.ORDER:
      return [
        QuoteStatus.ORDER_DRAFT_CUSTOMER,
        QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
        QuoteStatus.ORDER_ORDERED,
        QuoteStatus.ORDER_ACCEPTED,
        QuoteStatus.ORDER_CANCELED,
        QuoteStatus.ORDER_SHIPPED_PARTIAL,
        QuoteStatus.ORDER_SHIPPED_COMPLETE,
      ];
    case QuoteEntityType.QUOTE:
    default:
      return [
        QuoteStatus.DRAFT,
        QuoteStatus.OPEN,
        ...(excludeImported ? [] : [QuoteStatus.EDITABLE_BY_CUSTOMER]),
        QuoteStatus.WON,
        QuoteStatus.PUNCHED_OUT,
        QuoteStatus.LOST,
      ];
  }
};

interface IVendorNames {
  vendorName?: string | null;
  vendorLocationName?: string | null;
}

// Mirrors VendorNameCell so the Excel export matches what the grid shows; older quotes predate
// the stored vendor location name.
export const getVendorDisplayName = (quote?: IVendorNames | null) =>
  quote?.vendorLocationName || quote?.vendorName;

interface IColumnGateInput {
  isAlternateVersionEnabled: boolean;
  isAlternateExportEnabled: boolean;
  hasPermission: (permission: AccessPermissions) => boolean;
}

// FileNet status follows its send button's role so a user who can never send sees no column of
// dashes; the Trade Agreement columns are read-only SPA info, shown without the SEND_AS_SPA role.
export const getColumnGates = ({
  isAlternateVersionEnabled,
  isAlternateExportEnabled,
  hasPermission,
}: IColumnGateInput) => ({
  showFilenetStatus: isAlternateVersionEnabled && hasPermission(AccessPermissions.SEND_TO_FILENET),
  showTradeAgreementInfo: isAlternateExportEnabled,
});
